import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { Device } from '@twilio/voice-sdk';
import IncomingCallDialog from './IncomingCallDialog';
import ScreenCallPanel from './ScreenCallPanel';
import { screenCallStore } from '@/lib/screenCallStore';
import { PostCallSummaryDialog } from './PostCallSummaryDialog';
import { GlobalDialer } from './GlobalDialer';
import { DialerBar } from './DialerBar';
import { useFirstCallMilestone } from '@/hooks/useMilestone';
import { trackClientEvent } from '@/lib/lifecycleEvents';
import { getVoiceErrorMessage, isVoiceTokenError } from '@/lib/voiceErrors';
import {
  EMPTY_SLOTS, callSlotsReducer, decideIncoming, hasRingingWaiting, canSwap as canSwapSlots,
  isLoggableCallSid, missedStatusFor, ringingLegsForEndedCall, hasRingingInbound,
  type CallLeg, type CallSlots, type CallSlotsAction, type EndReason, type VoiceProvider,
} from '@/lib/voice/callSlots';
import { TwilioVoiceCall, type VoiceCall } from '@/lib/voice/voiceCall';
import { TelnyxVoiceClient, type TelnyxOffer, type TelnyxVoiceCall } from '@/lib/voice/telnyxVoiceClient';
import { CallWaitingBanner } from './CallWaitingBanner';
import { io } from 'socket.io-client';

/**
 * Retry an async operation with exponential backoff. Used to absorb transient
 * Twilio Voice SDK script-load / registration failures (108 hits/week pre-fix
 * for "Failed to load the JS script of the agent"). Backoff: 500ms, 2s, 8s.
 */
async function withRetry<T>(fn: () => Promise<T>, attempts = 3, baseMs = 500): Promise<T> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (i < attempts - 1) {
        await new Promise((r) => setTimeout(r, baseMs * Math.pow(4, i)));
      }
    }
  }
  throw lastErr;
}

// Twilio Voice Device interface
declare global {
  interface Window {
    Twilio: any;
  }
}

interface CallState {
  isActive: boolean;
  number: string;
  callSid?: string;
  startTime?: Date;
  endTime?: Date;
  status: 'idle' | 'connecting' | 'ringing' | 'connected' | 'on-hold' | 'ended' | 'failed';
  duration?: number;
  direction: 'inbound' | 'outbound';
  contact?: {
    id: number;
    name: string;
    phone: string;
    avatar?: string;
  };
  callerId?: string;
  fromNumber?: string;
  toNumber?: string;
  provider?: VoiceProvider;
  recording?: {
    isRecording: boolean;
    recordingSid?: string;
    autoRecord?: boolean;
  };
  transcription?: {
    isEnabled: boolean;
    transcript?: string;
    isLive?: boolean;
  };
  notes?: string;
  tags?: string[];
  disposition?: string;
}

interface VoiceSettings {
  autoRecord: boolean;
  transcriptionEnabled: boolean;
  preferredCallerId?: string;
  simultaneousRing: boolean;
  voicemailEnabled: boolean;
  businessHoursEnabled: boolean;
  businessHours?: {
    start: string;
    end: string;
    timezone: string;
  };
}

interface VoiceContextType {
  // Device Status
  deviceStatus: 'loading' | 'ready' | 'error' | 'disconnected';
  isDeviceReady: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'connecting';
  callState: CallState;
  availableNumbers: string[];
  
  // Call Methods
  makeCall: (phoneNumber: string, options?: { callerId?: string; fromNumber?: string }) => Promise<string>;
  endCall: () => void;
  hangUp: () => void;
  answerCall: () => void;
  rejectCall: () => void;
  
  // Call Controls
  toggleMute: () => void;
  toggleHold: () => void;
  toggleSpeaker: () => void;
  sendDigits: (digits: string) => void;
  setVolume: (volume: number) => void;
  
  // Call State Properties
  isMuted: boolean;
  isOnHold: boolean;
  isSpeakerOn: boolean;
  volume: number;
  callQuality: 'good' | 'fair' | 'poor' | null;
  callRef: React.MutableRefObject<any>;
  
  // Recording Controls
  startRecording: () => void;
  stopRecording: () => void;
  
  // Global Dialer Controls
  showDialer: (phoneNumber?: string) => void;
  hideDialer: () => void;
  isDialerVisible: boolean;
  dialerPhoneNumber: string;
  
  // Settings
  voiceSettings: VoiceSettings;
  updateVoiceSettings: (settings: Partial<VoiceSettings>) => void;

  // Call waiting: the other call (ringing over the active one, or on hold)
  waitingCall: WaitingCallInfo | null;
  /** Hold the active call and answer the waiting one. */
  holdAndAnswer: () => Promise<void>;
  /** Decline the waiting call (logged as missed). */
  declineWaiting: () => void;
  /** Both calls answered: hold the active one, resume the other. */
  swapCalls: () => Promise<void>;
  canSwap: boolean;
  /** The in-call overlay shows its own banner; returns the unregister. */
  registerCallWaitingBanner: () => () => void;
}

export interface WaitingCallInfo {
  number: string;
  callerName?: string;
  provider: VoiceProvider;
  status: 'ringing' | 'on-hold' | 'connected' | 'connecting';
}

export const VoiceContext = createContext<VoiceContextType | null>(null);

interface VoiceProviderProps {
  children: React.ReactNode;
}


export function GlobalVoiceProvider({ children }: VoiceProviderProps) {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { trackFirstCall } = useFirstCallMilestone();

  // State
  const [deviceStatus, setDeviceStatus] = useState<'loading' | 'ready' | 'error' | 'disconnected'>('loading');
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'connecting'>('disconnected');
  // Calls: one active plus an optional waiting/held one (lib/voice/callSlots).
  // slotsRef is updated synchronously so SDK event handlers never read a stale copy.
  const [slots, setSlots] = useState<CallSlots>(EMPTY_SLOTS);
  const slotsRef = useRef<CallSlots>(EMPTY_SLOTS);
  const dispatchSlots = useCallback((action: CallSlotsAction) => {
    const next = callSlotsReducer(slotsRef.current, action);
    if (next !== slotsRef.current) {
      slotsRef.current = next;
      setSlots(next);
    }
  }, []);
  const callsRef = useRef(new Map<string, VoiceCall>());
  const declinedRef = useRef(new Set<string>());
  const legSeqRef = useRef(0);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [lastEndTime, setLastEndTime] = useState<Date | undefined>(undefined);
  const telnyxRef = useRef<TelnyxVoiceClient | null>(null);
  const handleIncomingCallRef = useRef<(call: any) => void>(() => {});
  const handleTelnyxIncomingRef = useRef<(call: TelnyxVoiceCall, offer: TelnyxOffer | null) => void>(() => {});
  const [callWaitingBanners, setCallWaitingBanners] = useState(0);
  const registerCallWaitingBanner = useCallback(() => {
    setCallWaitingBanners((n) => n + 1);
    return () => setCallWaitingBanners((n) => n - 1);
  }, []);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>({
    autoRecord: false,
    transcriptionEnabled: false,
    simultaneousRing: false,
    voicemailEnabled: true,
    businessHoursEnabled: false
  });
  const [availableNumbers, setAvailableNumbers] = useState<string[]>([]);

  // Call control state
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);
  const [volume, setVolume] = useState(50);

  // Incoming call dialog state
  const [showIncomingDialog, setShowIncomingDialog] = useState(false);
  const [incomingCallerInfo, setIncomingCallerInfo] = useState<{
    number: string;
    name?: string;
    location?: string;
    source?: string;
    company?: string;
    callSid: string;
    /** The org number that was dialled (shown as the line tag). */
    calledNumber?: string;
  } | null>(null);
  const [callerInsights, setCallerInsights] = useState<any | null>(null);

  // Post-call summary dialog state
  const [showPostCallSummary, setShowPostCallSummary] = useState(false);
  const [completedCallSid, setCompletedCallSid] = useState<string | null>(null);
  const dbCallSidRef = useRef<string | null>(null);

  // Active call dialer state
  const [showActiveCallDialer, setShowActiveCallDialer] = useState(false);

  // Global dialer widget state
  const [isDialerVisible, setIsDialerVisible] = useState(false);
  const [dialerPhoneNumber, setDialerPhoneNumber] = useState('');

  const deviceRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [callQuality, setCallQuality] = useState<'good' | 'fair' | 'poor' | null>(null);
  const activeWarningsRef = useRef<Set<string>>(new Set());

  // Initialize Twilio Voice SDK - deferred for faster initial page load
  useEffect(() => {
    if (!isAuthenticated) return;
    
    let idleCallbackId: number | undefined;
    let timeoutId: NodeJS.Timeout | undefined;
    
    // Defer initialization using requestIdleCallback for better performance
    // This allows the UI to render first, then initialize voice in the background
    const deferredInit = () => {
      console.log('Initializing Twilio Voice SDK (deferred)...');
      initializeDevice();
    };
    
    // Use requestIdleCallback if available, otherwise fallback to setTimeout
    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(deferredInit, { timeout: 3000 });
    } else {
      // Fallback: wait 500ms after mount to allow UI to settle
      timeoutId = setTimeout(deferredInit, 500);
    }

    // Re-register device when network comes back online
    const handleOnline = () => {
      console.log('🌐 [VOICE] Network restored, checking device state...');
      if (deviceRef.current) {
        const state = (deviceRef.current as any).state;
        if (state !== 'registered') {
          console.log('🔄 [VOICE] Device not registered, re-initializing...');
          initializeDevice();
        }
      } else {
        initializeDevice();
      }
    };
    const handleOffline = () => {
      console.log('📴 [VOICE] Network lost');
      setConnectionStatus('disconnected');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      // Cancel pending initialization
      if (idleCallbackId !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      
      // Cleanup token refresh interval
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
        console.log('🧹 [TOKEN] Cleaned up token refresh interval');
      }
      
      if (deviceRef.current) {
        try {
          const state = deviceRef.current.state;
          if (state === 'registered') {
            deviceRef.current.unregister().catch((err: any) => {
              console.warn('Error unregistering device:', err);
            });
            console.log('🧹 [VOICE] Device unregistered');
          } else {
            deviceRef.current.destroy();
            console.log('🧹 [VOICE] Device destroyed (was not registered)');
          }
        } catch (error) {
          console.warn('Error cleaning up device:', error);
        }
      }
    };
  }, [isAuthenticated]);

  // Re-apply when the dialer's device picker changes a preference.
  useEffect(() => {
    const onChange = () => {
      const device = deviceRef.current as any;
      if (!device) return;
      const mic = localStorage.getItem('preferredMicrophone');
      const speaker = localStorage.getItem('preferredSpeaker');
      if (device.audio) {
        if (mic === 'default') device.audio.unsetInputDevice?.().catch?.(() => {});
        if (speaker === 'default') {
          device.audio.speakerDevices?.set('default').catch?.(() => {});
          device.audio.ringtoneDevices?.set('default').catch?.(() => {});
        }
      }
      applyAudioPreferences(device);
    };
    window.addEventListener('voice-audio-prefs-changed', onChange);
    return () => window.removeEventListener('voice-audio-prefs-changed', onChange);
  }, []);

  // Apply saved audio preferences from localStorage
  const applyAudioPreferences = async (device: any) => {
    try {
      const preferredMicrophone = localStorage.getItem('preferredMicrophone');
      const preferredSpeaker = localStorage.getItem('preferredSpeaker');
      
      // Set microphone if saved
      if (preferredMicrophone && preferredMicrophone !== 'default' && device.audio) {
        try {
          await device.audio.setInputDevice(preferredMicrophone);
          console.log('🎤 Applied saved microphone preference:', preferredMicrophone);
        } catch (error) {
          console.warn('Failed to apply saved microphone preference:', error);
        }
      }
      
      // Set speaker if saved
      if (preferredSpeaker && preferredSpeaker !== 'default' && device.audio) {
        try {
          await device.audio.speakerDevices.set(preferredSpeaker);
          await device.audio.ringtoneDevices.set(preferredSpeaker);
          console.log('🔊 Applied saved speaker preference:', preferredSpeaker);
        } catch (error) {
          console.warn('Failed to apply saved speaker preference:', error);
        }
      }
    } catch (error) {
      console.error('Error applying audio preferences:', error);
    }
  };

  // Initialize Twilio Device
  const initializeDevice = async () => {
    const loadStartTime = performance.now();
    try {
      setDeviceStatus('loading');
      setConnectionStatus('connecting');
      
      // Don't request microphone on app open - only when making a call
      // This prevents error popup when user opens the app
      console.log('⏭️ Skipping microphone request on init - will request when making calls');
      
      // Get voice token
      console.log('Fetching voice token...');
      const response = await apiRequest('GET', '/api/voice/token');
      const { token } = response;
      
      if (!token) {
        throw new Error('Failed to get voice token');
      }
      
      console.log('Creating Twilio Device...');
      
      // allowIncomingWhileBusy: a second inbound call reaches us as call
      // waiting instead of being dropped by the SDK.
      const device = new Device(token, { allowIncomingWhileBusy: true });
      
      deviceRef.current = device;
      console.log('Twilio Device created successfully, setting up event listeners...');
      
      // Setup device event listeners (Twilio 2.x correct events)
      device.on('registered', () => {
        console.log('✅ Twilio Device is registered and ready for calls');
        try {
          if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
            Notification.requestPermission().catch(() => {});
          }
          // Let the OS open tel: links in this dialer (browser asks the user once).
          if (window.isSecureContext && 'registerProtocolHandler' in navigator) {
            navigator.registerProtocolHandler('tel', `${window.location.origin}/voice-dialer?tel=%s`);
          }
        } catch (err) {
          console.warn('Notification / tel: handler setup skipped:', err);
        }
        setDeviceStatus('ready');
        setConnectionStatus('connected');
        trackClientEvent('voice.agent_loaded', {
          latency_ms: Math.round(performance.now() - loadStartTime),
        });
      });

      device.on('unregistered', () => {
        console.log('📴 Twilio Device is unregistered');
        setDeviceStatus('disconnected');
        setConnectionStatus('disconnected');
      });

      device.on('error', (error: any) => {
        console.error('❌ Twilio Device error:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        // Handle token-related errors
        if (isVoiceTokenError(error)) {
          console.error('🔄 [TOKEN] Token error detected, attempting refresh:', error.code, error.message);
          refreshToken();
        } else if (error.message?.includes('Device not found')) {
          // Audio device no longer available (e.g. headset unplugged, "communications" device removed)
          // Clear stale preference and continue — device still works with default audio
          console.warn('⚠️ [VOICE] Saved audio device unavailable, falling back to default:', error.message);
          localStorage.removeItem('preferredSpeaker');
          localStorage.removeItem('preferredMicrophone');
        } else {
          setDeviceStatus('error');
          setConnectionStatus('disconnected');
          toast({
            title: "Voice Error",
            description: getVoiceErrorMessage(error),
            variant: "destructive",
          });
        }
      });

      device.on('incoming', (call: any) => {
        console.log('📞 Incoming call received:', call);
        handleIncomingCallRef.current(call);
      });

      device.on('tokenWillExpire', () => {
        console.log('⚠️ [TOKEN] Device warning: token will expire, refreshing immediately...');
        refreshToken();
      });

      // Register the device
      console.log('🔗 Registering Twilio Device...');
      // device.register() returns a Promise<void> that rejects with a Twilio error
      // object containing { code, message } when registration fails (e.g. invalid token,
      // network error, or Twilio service unavailable). Without a .catch() this becomes
      // an unhandled promise rejection captured by Sentry as LIMEPHONE-FRONTEND-8B.
      // Retry SDK registration only — token fetch above has legitimate auth-fail
      // semantics we don't want to retry.
      withRetry(() => device.register()).catch((err: any) => {
        console.error('❌ [VOICE] Device registration failed after retries:', err?.code, err?.message);
        setDeviceStatus('error');
        setConnectionStatus('disconnected');
        trackClientEvent('voice.agent_load_failed', {
          error: String(err?.message ?? err),
          latency_ms: Math.round(performance.now() - loadStartTime),
        });
        // Token errors are already handled by the 'error' event listener; only show
        // a toast for non-token registration failures to avoid duplicate messages.
        const isTokenError = err?.code === 20104 || err?.code === 20103 ||
          err?.message?.includes('token');
        if (!isTokenError) {
          toast({
            title: "Voice Setup Error",
            description: "Could not register the voice device. Please refresh the page.",
            variant: "destructive",
          });
        }
      });

      // Apply saved audio preferences after registration
      applyAudioPreferences(device);

      // Setup automatic token refresh every 45 minutes (before 55-minute server expiration)
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
      tokenRefreshIntervalRef.current = setInterval(() => {
        console.log('⏰ [TOKEN] Auto-refreshing token (45-minute interval)');
        refreshToken();
      }, 45 * 60 * 1000); // 45 minutes
      
      // Add a timeout to check if registration works within 5 seconds
      setTimeout(() => {
        if (deviceRef.current && deviceStatus !== 'ready') {
          console.log('⚠️ Device not registered yet after 5 seconds - will retry when event fires');
        }
      }, 5000);

    } catch (error: any) {
      console.error('Failed to initialize voice device:', error);
      setDeviceStatus('error');
      setConnectionStatus('disconnected');
      trackClientEvent('voice.agent_load_failed', {
        error: String(error?.message ?? error),
        latency_ms: Math.round(performance.now() - loadStartTime),
      });
      toast({
        title: "Voice Setup Error",
        description: "Failed to initialize voice calling. Please check your microphone permissions.",
        variant: "destructive",
      });
    }
  };

  // Refresh token when it expires
  const refreshToken = async () => {
    try {
      const response = await apiRequest('POST', '/api/voice/token-refresh');
      const { token } = response;
      
      if (deviceRef.current && token) {
        await deviceRef.current.updateToken(token);
        console.log('✅ [TOKEN] Voice token refreshed successfully');
      } else {
        console.error('❌ [TOKEN] Failed to refresh: device or token missing');
      }
    } catch (error) {
      console.error('❌ [TOKEN] Failed to refresh voice token:', error);
      // Attempt re-initialize if token fetch fails
      setTimeout(() => {
        console.log('🔄 [TOKEN] Attempting device re-initialization after token refresh failure');
        initializeDevice();
      }, 3000);
    }
  };

  // Lookup caller information
  const lookupCallerInfo = async (phoneNumber: string) => {
    try {
      // Try to find caller in leads/contacts
      const response = await apiRequest('GET', `/api/leads/lookup-by-phone?phone=${encodeURIComponent(phoneNumber)}`);
      if (response.contact) {
        return {
          name: `${response.contact.firstName} ${response.contact.lastName}`.trim(),
          location: response.contact.address || response.contact.city || response.contact.state,
          source: response.contact.source || 'contact',
          company: response.contact.company
        };
      }
    } catch (error) {
      console.log('No contact found for phone number:', phoneNumber);
    }
    
    // Default caller info if not found
    return {
      name: undefined,
      location: undefined,
      source: 'external',
      company: undefined
    };
  };

  // Record a missed inbound call (declined, unanswered, or refused as busy).
  // The server accepts only a real Twilio/Telnyx call id and an org-owned To.
  const recordMissedCall = async (callSid: string | undefined, from: string, to: string, status: string, startTime?: number) => {
    if (!isLoggableCallSid(callSid)) {
      console.log(`📞 Not logging missed call without a server call id (${callSid ?? 'none'})`);
      return;
    }
    try {
      console.log(`📞 Recording missed call: ${callSid} from ${from} to ${to} with status ${status}`);
      await apiRequest('POST', '/api/calls/record-missed', {
        callSid,
        from,
        to,
        status,
        direction: 'inbound',
        duration: 0,
        startTime: startTime ? new Date(startTime) : new Date(),
        endTime: new Date(),
      });
      console.log(`✅ Missed call recorded: ${callSid}`);
    } catch (error) {
      console.error('❌ Failed to record missed call:', error);
    }
  };

  // Close the service worker's incoming-call notifications (web push, tag
  // "incoming_call-<sid>") once no inbound call is ringing here any more.
  const closeRingingNotifications = () => {
    setTimeout(() => {
      if (hasRingingInbound(slotsRef.current)) return;
      try {
        navigator.serviceWorker?.controller?.postMessage({ type: 'close-notifications', tagPrefix: 'incoming_call-' });
      } catch { /* no service worker */ }
    }, 0);
  };

  // The caller hung up while it rang and the server says so (call:ended), but
  // the SDK has not ended the call (or never will): stop ringing and clear it.
  const dismissEndedRinging = (msg: any) => {
    // "Screen call" in progress for this caller: close the screening panel.
    const screening = screenCallStore.get().active;
    if (screening && msg?.callSid && screening.callSid === msg.callSid) {
      screenCallStore.clear();
      toast({ title: 'Caller hung up' });
    }
    for (const key of ringingLegsForEndedCall(slotsRef.current, msg)) {
      const call = callsRef.current.get(key);
      console.log(`📴 Caller hung up before answer (${msg?.callSid}); clearing ringing call ${key}`);
      if (call) call.dismiss();
      else finalizeLeg(key, 'canceled');
    }
  };
  const dismissEndedRingingRef = useRef(dismissEndedRinging);
  dismissEndedRingingRef.current = dismissEndedRinging;

  /**
   * Is this ringing leg still answerable? If the caller already hung up, clear
   * it and say so instead of answering into nothing.
   */
  const answerableOrGone = (leg: CallLeg | null, call: VoiceCall | undefined): leg is CallLeg => {
    if (leg && call && leg.status === 'ringing' && leg.direction === 'inbound' && !call.isGone()) return true;
    if (leg && leg.status === 'ringing' && leg.direction === 'inbound') {
      if (call) call.dismiss();
      else finalizeLeg(leg.key, 'canceled');
    }
    setShowIncomingDialog(false);
    toast({ title: 'Caller hung up', description: 'The call ended before it was answered.' });
    return false;
  };

  const findLeg = (key: string): CallLeg | null => {
    const s = slotsRef.current;
    return s.active?.key === key ? s.active : s.waiting?.key === key ? s.waiting : null;
  };

  // A call ended (either side, any provider). Runs once per leg.
  const finalizeLeg = (key: string, reason: EndReason, error?: any) => {
    const leg = findLeg(key);
    const call = callsRef.current.get(key);
    callsRef.current.delete(key);
    const declined = declinedRef.current.delete(key);
    dispatchSlots({ type: 'ended', key });
    if (!leg) return;
    console.log(`📴 [${leg.provider}] call ${leg.callSid ?? key} ended (${reason})`);
    if (call) call.mute(false);
    if (leg.direction === 'inbound' && !leg.connectedAt) closeRingingNotifications();

    const missed = missedStatusFor(leg, reason, declined);
    if (missed) {
      recordMissedCall(leg.callSid, leg.number, leg.toNumber || '', missed, leg.startTime);
    } else if (leg.callSid && (leg.connectedAt || leg.direction === 'outbound') && !leg.callSid.startsWith('TELNYX-')) {
      // Send call completion to server so DB gets updated with duration and status
      const endTime = new Date();
      const durationSeconds = Math.round((endTime.getTime() - (leg.connectedAt ?? leg.startTime)) / 1000);
      console.log(`📊 [CALL END] Sending completion update: SID=${leg.callSid}, duration=${durationSeconds}s`);
      apiRequest('PATCH', `/api/voice/calls/${leg.callSid}/complete`, {
        status: 'completed',
        duration: durationSeconds,
        endTime: endTime.toISOString()
      }).then(() => {
        console.log(`✅ [CALL END] Call ${leg.callSid} marked completed with ${durationSeconds}s duration`);
      }).catch((err: any) => {
        console.error(`❌ [CALL END] Failed to update call completion:`, err);
      });
    }

    if (reason === 'error') {
      toast({
        title: leg.direction === 'inbound' ? "Incoming Call Error" : "Call Error",
        description: getVoiceErrorMessage(error),
        variant: "destructive",
      });
    }

    // Post-call summary once no call is left up (never over a live call).
    const remaining = slotsRef.current;
    const othersUp = (remaining.active && remaining.active.key !== key) || (remaining.waiting && remaining.waiting.key !== key);
    const summaryCallSid = dbCallSidRef.current || (isLoggableCallSid(leg.callSid) ? leg.callSid : null);
    if (leg.connectedAt && !othersUp && summaryCallSid) {
      dbCallSidRef.current = null;
      setTimeout(() => {
        setCompletedCallSid(summaryCallSid);
        setShowPostCallSummary(true);
      }, 2000);
    }
  };

  // Attach the normalised events every call gets, whatever its provider.
  const wireCall = (key: string, call: VoiceCall) => {
    callsRef.current.set(key, call);
    call.on('ringing', () => {
      const leg = findLeg(key);
      if (leg?.direction === 'outbound' && leg.status === 'connecting') {
        dispatchSlots({ type: 'update', key, patch: { status: 'ringing' } });
      }
    });
    call.on('accepted', () => {
      const leg = findLeg(key);
      const sid = call.callSid();
      if (sid && leg && leg.callSid !== sid && leg.provider === 'twilio' && leg.direction === 'inbound') {
        dispatchSlots({ type: 'update', key, patch: { callSid: sid } });
      }
      dispatchSlots({ type: 'answered', key, at: Date.now() });
      if (call.provider === 'telnyx' && leg?.direction === 'inbound' && sid) {
        telnyxRef.current?.reportAnswered(sid);
      }
    });
    call.on('warning', (name: string) => {
      if (slotsRef.current.active?.key !== key) return;
      console.log('⚠️ [QUALITY] Warning:', name);
      activeWarningsRef.current.add(name);
      setCallQuality(deriveQuality(activeWarningsRef.current));
    });
    call.on('warning-cleared', (name: string) => {
      if (slotsRef.current.active?.key !== key) return;
      console.log('✅ [QUALITY] Warning cleared:', name);
      activeWarningsRef.current.delete(name);
      setCallQuality(deriveQuality(activeWarningsRef.current));
    });
    call.on('ended', (reason, error) => finalizeLeg(key, reason, error));
  };

  // An inbound call from either provider: ring it, put it in call waiting, or
  // (third call) refuse it as busy and log it as missed.
  const registerIncoming = (call: VoiceCall, info: { from: string; to: string; callSid?: string; callerName?: string; serverHold: boolean }) => {
    const decision = decideIncoming(slotsRef.current);
    console.log(`📞 [${call.provider}] incoming call`, { from: info.from, to: info.to, callSid: info.callSid, decision });

    if (decision === 'reject') {
      try { call.reject(); } catch (err) { console.error('Failed to reject third call:', err); }
      const status = missedStatusFor({ direction: 'inbound' }, 'rejected', true, true);
      if (status) recordMissedCall(info.callSid, info.from, info.to, status);
      return;
    }

    const key = `${call.provider}-${++legSeqRef.current}`;
    const leg: CallLeg = {
      key,
      provider: call.provider,
      callSid: info.callSid,
      direction: 'inbound',
      number: info.from,
      fromNumber: info.from,
      toNumber: info.to,
      callerName: info.callerName,
      status: 'ringing',
      startTime: Date.now(),
      serverHold: info.serverHold,
    };
    wireCall(key, call);
    dispatchSlots({ type: 'incoming', leg });

    // The caller the user just screened and chose to Answer: the server dialled
    // this leg back, so pick it up without a second tap.
    if (decision !== 'wait' && info.from && screenCallStore.consumeAutoAccept(info.from)) {
      try {
        call.accept();
        dispatchSlots({ type: 'answered', key, at: Date.now() });
        setShowActiveCallDialer(true);
      } catch (err) {
        console.error('Auto-accept of screened call failed:', err);
      }
      screenCallStore.clear();
      return;
    }

    // Desktop notification so a ringing call isn't missed in a background tab.
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && document.hidden) {
        const n = new Notification(decision === 'wait' ? 'Call waiting' : 'Incoming call', {
          body: info.from ? `From ${info.callerName || info.from}` : 'Unknown caller',
          tag: `call-${info.callSid || key}`,
          requireInteraction: true,
        });
        n.onclick = () => { window.focus(); n.close(); };
        call.on('accepted', () => n.close());
        call.on('ended', () => n.close());
      }
    } catch (err) {
      console.error('Incoming call notification failed:', err);
    }

    // Name for the call-waiting banner (the ringing dialog loads full insights).
    if (decision === 'wait' && !info.callerName && info.from) {
      lookupCallerInfo(info.from).then((c) => {
        if (c.name) dispatchSlots({ type: 'update', key, patch: { callerName: c.name } });
      });
    }
  };

  const handleIncomingCall = (raw: any) => {
    const { From: from, To: to, CallSid: callSid } = raw.parameters || {};
    registerIncoming(new TwilioVoiceCall(raw), { from, to, callSid, serverHold: true });
  };
  handleIncomingCallRef.current = handleIncomingCall;

  const handleTelnyxIncoming = (call: TelnyxVoiceCall, offer: TelnyxOffer | null) => {
    const opts = call.raw?.options || {};
    registerIncoming(call, {
      from: offer?.from || opts.remoteCallerNumber || '',
      to: offer?.to || opts.remoteCallerNumber || '',
      callSid: offer?.callSid,
      callerName: offer?.callerName || undefined,
      serverHold: offer?.callControl === true,
    });
  };
  handleTelnyxIncomingRef.current = handleTelnyxIncoming;

  // Telnyx web calling: only orgs with a Telnyx number get a credential (the
  // token route says enabled:false otherwise and the SDK is never loaded).
  useEffect(() => {
    if (!isAuthenticated) return;
    const client = new TelnyxVoiceClient(apiRequest, {
      onIncoming: (call, offer) => handleTelnyxIncomingRef.current(call, offer),
      onStatus: (s) => console.log(`📡 [TELNYX] web calling ${s}`),
    });
    telnyxRef.current = client;
    const startTimer = setTimeout(() => {
      client.start()
        .then((on) => console.log(on ? '✅ [TELNYX] web calling enabled' : 'ℹ️ [TELNYX] web calling not enabled for this user'))
        .catch((err) => console.error('❌ [TELNYX] web calling failed to start:', err));
    }, 1500);
    const onUnload = () => client.sayOffline();
    window.addEventListener('pagehide', onUnload);
    return () => {
      clearTimeout(startTimer);
      window.removeEventListener('pagehide', onUnload);
      client.stop();
      if (telnyxRef.current === client) telnyxRef.current = null;
    };
  }, [isAuthenticated]);

  // Server-side "the caller hung up before anyone answered" (status webhook →
  // services/ringingLegCancel). Backs up the SDK's own cancel event.
  useEffect(() => {
    if (!isAuthenticated) return;
    let token: string | null = null;
    try { token = localStorage.getItem('token'); } catch { /* storage blocked */ }
    if (!token) return;
    const socket = io({ auth: { token }, reconnectionAttempts: 5, timeout: 10000 });
    socket.on('connect', () => socket.emit('auth', { token }));
    socket.on('call:ended', (msg: any) => dismissEndedRingingRef.current(msg));
    return () => { socket.disconnect(); };
  }, [isAuthenticated]);

  // The IncomingCallDialog follows whichever call is ringing in the active
  // slot — a first call, or a waiting call promoted when the other one ended.
  const ringingKey = slots.active && slots.active.status === 'ringing' && slots.active.direction === 'inbound'
    ? slots.active.key : null;
  useEffect(() => {
    if (!ringingKey) {
      setShowIncomingDialog(false);
      setIncomingCallerInfo(null);
      return;
    }
    const leg = slotsRef.current.active;
    if (!leg) return;
    const from = leg.number;
    const callSid = leg.callSid || leg.key;
    let cancelled = false;
    // Clear any stale insights first
    setCallerInsights(null);
    setIncomingCallerInfo({ number: from, name: leg.callerName, callSid, source: 'external', calledNumber: leg.toNumber });
    setShowIncomingDialog(true);
    (async () => {
      try {
        // Fetch comprehensive caller insights (relationship history and spam detection)
        const insights = await apiRequest('GET', `/api/caller-insights/${encodeURIComponent(from)}`);
        if (cancelled) return;
        setCallerInsights(insights);
        setIncomingCallerInfo({
          number: from,
          name: insights.lead?.fullName || insights.callerIdInfo.callerName || leg.callerName,
          location: insights.callerIdInfo.location ?
            `${insights.callerIdInfo.location.city}, ${insights.callerIdInfo.location.state}` :
            undefined,
          source: insights.lead ? 'contact' : 'external',
          company: insights.lead?.company,
          callSid,
          calledNumber: leg.toNumber
        });
      } catch (error) {
        console.error('Failed to fetch caller insights:', error);
        if (cancelled) return;
        setCallerInsights(null);
        // Fallback to basic caller lookup
        const callerInfo = await lookupCallerInfo(from);
        if (cancelled) return;
        setIncomingCallerInfo({
          number: from,
          name: callerInfo.name || leg.callerName,
          location: callerInfo.location,
          source: callerInfo.source,
          company: callerInfo.company,
          callSid,
          calledNumber: leg.toNumber
        });
      }
    })();
    return () => { cancelled = true; };
  }, [ringingKey]);

  // Per-active-call UI state: mute mirrors the call, quality restarts, the
  // active-call dialer closes when nothing is up.
  const activeKey = slots.active?.key ?? null;
  useEffect(() => {
    const call = activeKey ? callsRef.current.get(activeKey) : undefined;
    callRef.current = call?.raw ?? null;
    setIsMuted(call ? call.isMuted() : false);
    activeWarningsRef.current = new Set();
    setCallQuality(null);
    if (!activeKey) setShowActiveCallDialer(false);
  }, [activeKey]);

  // One-second tick for the duration display while a call is up.
  const anyCallUp = !!slots.active;
  useEffect(() => {
    if (!anyCallUp) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [anyCallUp]);

  const deriveQuality = (warnings: Set<string>): 'good' | 'fair' | 'poor' => {
    if (warnings.has('high-packet-loss') || warnings.has('ice-connectivity-lost')) return 'poor';
    if (warnings.size > 0) return 'fair';
    return 'good';
  };

  // Make outbound call
  const makeCall = async (phoneNumber: string, options?: { callerId?: string; fromNumber?: string }): Promise<string> => {
    console.log('📞 [MAKE CALL] Initiating call to', phoneNumber, options);

    if (slotsRef.current.active) {
      throw new Error('Finish the current call before placing another.');
    }

    // Validate caller ID is available
    // Only fall back automatically when there is exactly one number; with several,
    // silently picking the first one could call out from the wrong line.
    const callerIdToUse = options?.callerId || options?.fromNumber ||
      (availableNumbers.length === 1 ? availableNumbers[0] : undefined);
    if (!callerIdToUse) {
      throw new Error(availableNumbers.length === 0
        ? 'No phone numbers available for outbound calling. Please purchase a phone number first.'
        : 'Choose which number to call from.');
    }

    // A Telnyx caller ID goes through the single-use ticket bridge when
    // TELNYX_BROWSER_OUTBOUND is on; the server says which carrier to use.
    const telnyx = telnyxRef.current;
    const prepared = telnyx ? await telnyx.prepareOutbound(phoneNumber, callerIdToUse) : null;
    if (telnyx && prepared) {
      const call = telnyx.connect(prepared.sipUri, callerIdToUse);
      const tempCallSid = `TELNYX-${Date.now()}`;
      const key = `telnyx-${++legSeqRef.current}`;
      dispatchSlots({
        type: 'outgoing',
        leg: {
          key, provider: 'telnyx', callSid: tempCallSid, direction: 'outbound', number: phoneNumber,
          callerId: options?.callerId, fromNumber: callerIdToUse, toNumber: phoneNumber,
          status: 'connecting', startTime: Date.now(), serverHold: false,
        },
      });
      wireCall(key, call);
      // The bridge webhook writes the call row (the browser can't know its id).
      trackFirstCall(tempCallSid);
      console.log('📞 [MAKE CALL] Telnyx browser call placed via ticket bridge');
      return tempCallSid;
    }

    // Enhanced device readiness check
    if (!deviceRef.current) {
      throw new Error('Voice device is not initialized');
    }

    if (deviceStatus !== 'ready') {
      throw new Error(`Voice device is not ready (status: ${deviceStatus})`);
    }

    // Check device state directly
    const deviceState = (deviceRef.current as any).state;
    console.log('🔍 Device state check:', deviceState);

    if (deviceState !== 'registered') {
      throw new Error(`Device is not registered (state: ${deviceState})`);
    }

    try {
      // Generate a temporary call SID for UI tracking
      const tempCallSid = `BROWSER-${Date.now()}`;
      const key = `twilio-${++legSeqRef.current}`;

      dispatchSlots({
        type: 'outgoing',
        leg: {
          key, provider: 'twilio', callSid: tempCallSid, direction: 'outbound', number: phoneNumber,
          callerId: options?.callerId, fromNumber: callerIdToUse, toNumber: phoneNumber,
          status: 'connecting', startTime: Date.now(), serverHold: true,
        },
      });

      // For Twilio Voice SDK 2.x, connect() returns a Promise that resolves to a Call
      // The parameters need to be passed in a specific format for the TwiML App
      const connectParams = {
        params: {
          To: phoneNumber,
          CallerId: callerIdToUse,
          PhoneNumber: phoneNumber // Alternative parameter name
        }
      };

      console.log('🔗 Connecting with params:', connectParams);

      let raw: any;
      try {
        raw = await deviceRef.current.connect(connectParams);
      } catch (connectError) {
        dispatchSlots({ type: 'ended', key });
        throw connectError;
      }

      // Enhanced call object validation
      if (!raw || typeof raw !== 'object' || typeof raw.on !== 'function') {
        dispatchSlots({ type: 'ended', key });
        throw new Error('device.connect() did not return a call');
      }

      const call = new TwilioVoiceCall(raw);
      wireCall(key, call);
      if ((slotsRef.current as CallSlots).active?.key === key) callRef.current = raw;

      call.on('accepted', async () => {
        // Get the real Twilio call SID
        const realCallSid = raw.parameters?.CallSid || raw.sid || raw.callSid;
        console.log('📞 Real Call SID from accept event:', realCallSid);

        // Update database with real call SID if we have one
        if (realCallSid && realCallSid !== tempCallSid) {
          dispatchSlots({ type: 'update', key, patch: { callSid: realCallSid } });
          try {
            await apiRequest('PUT', `/api/voice/calls/${tempCallSid}`, {
              callSid: realCallSid,
              status: 'connected',
              connectTime: new Date().toISOString()
            });
            console.log('📊 Database updated with real Call SID:', realCallSid);
          } catch (dbError) {
            console.error('Failed to update call with real SID:', dbError);
          }
        }
      });

      // Log call to database immediately with browser-generated ID
      try {
        const callResponse = await apiRequest('POST', '/api/voice/calls', {
          callSid: tempCallSid,
          from: callerIdToUse,
          to: phoneNumber,
          direction: 'outbound',
          status: 'connecting'
        });
        console.log('📊 Call logged to database with temp SID:', tempCallSid);
        if (callResponse?.callId) {
          dbCallSidRef.current = String(callResponse.callId);
          console.log('📊 Stored DB call ID for post-call summary:', callResponse.callId);
        }

        // Track first call milestone
        trackFirstCall(tempCallSid);
      } catch (dbError) {
        console.error('Failed to log call to database:', dbError);
      }

      console.log('📞 [MAKE CALL] Browser call initiated, waiting for TwiML response');
      return tempCallSid;
    } catch (error) {
      console.error('Failed to make call:', error);
      throw error;
    }
  };

  // End (hang up) the active call. A call waiting or on hold stays; it moves
  // into the active slot.
  const endCall = () => {
    const leg = slotsRef.current.active;
    if (!leg) return;
    const call = callsRef.current.get(leg.key);
    if (call) call.hangup();
    else finalizeLeg(leg.key, 'hangup');
  };

  // Accept incoming call (dialog)
  const acceptCall = () => {
    const leg = slotsRef.current.active;
    const call = leg ? callsRef.current.get(leg.key) : undefined;
    if (leg && (leg.status !== 'ringing' || leg.direction !== 'inbound')) return; // answered, or not an inbound ring
    if (answerableOrGone(leg, call) && call) {
      console.log('📞 Accepting incoming call');
      try { call.accept(); } catch (err) { console.error('Accept failed:', err); answerableOrGone(null, undefined); return; }
      dispatchSlots({ type: 'answered', key: leg.key, at: Date.now() });
      setShowIncomingDialog(false);
      setShowActiveCallDialer(true);
    }
  };

  // Reject incoming call
  const rejectCall = () => {
    const leg = slotsRef.current.active;
    const call = leg ? callsRef.current.get(leg.key) : undefined;
    if (leg && call && leg.status === 'ringing') {
      console.log('🚫 Rejecting incoming call');
      declinedRef.current.add(leg.key);
      call.reject();
      setShowIncomingDialog(false);
    }
  };

  // Answer incoming call (in-call overlay)
  const answerCall = () => {
    const leg = slotsRef.current.active;
    const call = leg ? callsRef.current.get(leg.key) : undefined;
    if (leg && (leg.status !== 'ringing' || leg.direction !== 'inbound')) return; // answered, or not an inbound ring
    if (answerableOrGone(leg, call) && call) {
      try { call.accept(); } catch (err) { console.error('Accept failed:', err); answerableOrGone(null, undefined); return; }
      dispatchSlots({ type: 'answered', key: leg.key, at: Date.now() });
    }
  };

  // Mute/unmute call
  const toggleMute = () => {
    const leg = slotsRef.current.active;
    const call = leg ? callsRef.current.get(leg.key) : undefined;
    if (call) {
      const next = !call.isMuted();
      call.mute(next);
      setIsMuted(next);
    }
  };

  // Send DTMF digits
  const sendDigits = (digits: string) => {
    const leg = slotsRef.current.active;
    const call = leg ? callsRef.current.get(leg.key) : undefined;
    if (call) call.sendDigits(digits);
  };

  // Additional control methods
  const hangUp = () => endCall();

  /**
   * Put one call on hold or take it off. The server's conference hold routes
   * when they work for the call (Twilio; Telnyx ringing through a conference),
   * else the SDK's media hold. The held leg's mic is muted too, so with two
   * calls up the held party never hears the other conversation.
   */
  const setLegHold = async (leg: CallLeg, on: boolean): Promise<void> => {
    const call = callsRef.current.get(leg.key);
    if (leg.serverHold && isLoggableCallSid(leg.callSid)) {
      const endpoint = on ? `/api/calls/${leg.callSid}/hold` : `/api/calls/${leg.callSid}/resume`;
      const response = await apiRequest('POST', endpoint);
      console.log(`✅ Call ${on ? 'held' : 'resumed'}:`, response);
    } else if (call?.localHold) {
      await call.localHold(on);
    } else {
      throw new Error("This call can't be put on hold yet.");
    }
    if (call) {
      call.mute(on);
      if (slotsRef.current.active?.key === leg.key) setIsMuted(on);
    }
  };

  const toggleHold = async () => {
    const leg = slotsRef.current.active;
    if (!leg || (leg.status !== 'connected' && leg.status !== 'on-hold')) {
      console.log('⚠️ No active call to hold/resume');
      return;
    }

    const newHoldState = leg.status !== 'on-hold';
    try {
      console.log(`🔄 ${newHoldState ? 'Holding' : 'Resuming'} call ${leg.callSid}`);
      // A held call resumed while another call is live would talk over it:
      // resuming one means holding the other (that's Swap).
      const other = slotsRef.current.waiting;
      if (!newHoldState && other && other.status === 'connected') {
        await setLegHold(other, true);
        dispatchSlots({ type: 'setHeld', key: other.key, held: true });
      }
      await setLegHold(leg, newHoldState);
      dispatchSlots({ type: 'setHeld', key: leg.key, held: newHoldState });

      toast({
        title: newHoldState ? "Call On Hold" : "Call Resumed",
        description: newHoldState
          ? "Caller is now listening to hold music"
          : "You're back on the call",
        variant: "default",
      });
    } catch (error: any) {
      console.error('❌ Failed to toggle hold:', error);
      toast({
        title: "Hold Error",
        description: error?.message?.includes("can't be put on hold") ? error.message : "Failed to toggle call hold status",
        variant: "destructive",
      });
    }
  };

  // Call waiting: hold the current call and answer the waiting one.
  const holdAndAnswer = async () => {
    const s = slotsRef.current;
    if (!hasRingingWaiting(s) || !s.active || !s.waiting) return;
    const current = s.active;
    const next = s.waiting;
    const nextCall = callsRef.current.get(next.key);
    if (!nextCall) return;
    if (current.status === 'connected') {
      try {
        await setLegHold(current, true);
      } catch (error: any) {
        console.error('❌ Hold & answer: could not hold the current call:', error);
        toast({
          title: "Couldn't hold the current call",
          description: "The waiting call is still ringing. Decline it or end the current call to answer.",
          variant: "destructive",
        });
        return;
      }
    }
    // The waiting call may have hung up while the hold request was in flight.
    if (!callsRef.current.has(next.key) || nextCall.isGone()) {
      if (callsRef.current.has(next.key)) nextCall.dismiss();
      toast({ title: 'Caller hung up', description: 'The waiting call ended before it was answered.' });
      return;
    }
    nextCall.accept();
    dispatchSlots({ type: 'holdAndAnswer', at: Date.now() });
  };

  // Call waiting: decline the waiting call (logged as missed).
  const declineWaiting = () => {
    const w = slotsRef.current.waiting;
    if (!w || w.status !== 'ringing') return;
    const call = callsRef.current.get(w.key);
    declinedRef.current.add(w.key);
    if (call) call.reject();
    else finalizeLeg(w.key, 'rejected');
  };

  // Two answered calls: hold the current one, resume the other.
  const swapCalls = async () => {
    const s = slotsRef.current;
    if (!canSwapSlots(s) || !s.active || !s.waiting) return;
    const current = s.active;
    const other = s.waiting;
    try {
      if (current.status === 'connected') await setLegHold(current, true);
    } catch (error) {
      console.error('❌ Swap: could not hold the current call:', error);
      toast({ title: "Swap failed", description: "Couldn't put the current call on hold.", variant: "destructive" });
      return;
    }
    try {
      await setLegHold(other, false);
    } catch (error) {
      console.error('❌ Swap: could not resume the held call:', error);
      // Put the call we just held back so nobody is left stranded on hold.
      if (current.status === 'connected') await setLegHold(current, false).catch(() => {});
      toast({ title: "Swap failed", description: "Couldn't take the other call off hold.", variant: "destructive" });
      return;
    }
    dispatchSlots({ type: 'swap' });
  };

  // Arriving from a tel: link (registered protocol handler): open the dialer prefilled.
  useEffect(() => {
    if (!isAuthenticated) return;
    const params = new URLSearchParams(window.location.search);
    const tel = params.get('tel');
    if (!tel) return;
    const number = decodeURIComponent(tel).replace(/^tel:/i, '').replace(/[^\d+]/g, '');
    if (number) {
      setDialerPhoneNumber(number);
      setIsDialerVisible(true);
    }
    params.delete('tel');
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [isAuthenticated]);

  // Load available numbers
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadAvailableNumbers = async () => {
      try {
        const numbers = await apiRequest('GET', '/api/voice/available-numbers');
        setAvailableNumbers(numbers);
      } catch (error) {
        console.error('Failed to load available numbers:', error);
        setAvailableNumbers([]);
      }
    };

    loadAvailableNumbers();
  }, [isAuthenticated]);

  const toggleSpeaker = () => {
    setIsSpeakerOn(prev => !prev);
    // Browser audio routing would be handled here
  };

  const handleSetVolume = (newVolume: number) => {
    setVolume(newVolume);
    // Browser volume control would be handled here
  };

  // Global dialer control methods
  const showDialer = (phoneNumber?: string) => {
    setDialerPhoneNumber(phoneNumber || '');
    setIsDialerVisible(true);
  };

  const hideDialer = () => {
    setIsDialerVisible(false);
    setDialerPhoneNumber('');
  };

  const startRecording = async () => {
    console.log('Start recording requested');
    try {
      if (callState.callSid) {
        const response = await apiRequest('POST', `/api/voice/calls/${callState.callSid}/start-recording`);
        console.log('✅ Call recording started:', response);
        toast({
          title: "Recording Started",
          description: "Call is now being recorded",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast({
        title: "Recording Error",
        description: "Failed to start call recording",
        variant: "destructive",
      });
    }
  };

  const stopRecording = async () => {
    console.log('Stop recording requested');
    try {
      if (callState.callSid) {
        const response = await apiRequest('POST', `/api/voice/calls/${callState.callSid}/stop-recording`);
        console.log('✅ Call recording stopped:', response);
        toast({
          title: "Recording Stopped",
          description: "Call recording has been saved",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Failed to stop recording:', error);
      toast({
        title: "Recording Error", 
        description: "Failed to stop call recording",
        variant: "destructive",
      });
    }
  };

  // Remember when the last call dropped (callState.endTime when idle).
  const hadCall = !!slots.active;
  const prevHadCall = useRef(false);
  useEffect(() => {
    if (prevHadCall.current && !hadCall) setLastEndTime(new Date());
    prevHadCall.current = hadCall;
  }, [hadCall]);

  // The active call, in the shape consumers have always read.
  const active = slots.active;
  const callState: CallState = active ? {
    isActive: true,
    number: active.number,
    callSid: active.callSid,
    startTime: new Date(active.startTime),
    status: active.status,
    duration: active.connectedAt ? Math.max(0, Math.floor((nowTick - active.connectedAt) / 1000)) : 0,
    direction: active.direction,
    callerId: active.callerId,
    fromNumber: active.fromNumber,
    toNumber: active.toNumber,
    provider: active.provider,
  } : {
    isActive: false,
    number: '',
    status: 'idle',
    direction: 'outbound',
    endTime: lastEndTime,
  };
  const isOnHold = active?.status === 'on-hold';
  const waiting = slots.waiting;
  const waitingCall: WaitingCallInfo | null = waiting
    ? { number: waiting.number, callerName: waiting.callerName, provider: waiting.provider, status: waiting.status }
    : null;

  const contextValue: VoiceContextType = {
    deviceStatus,
    isDeviceReady: deviceStatus === 'ready',
    connectionStatus,
    callState,
    availableNumbers,
    makeCall,
    endCall,
    hangUp,
    answerCall,
    rejectCall,
    toggleMute,
    toggleHold,
    toggleSpeaker,
    sendDigits,
    setVolume: handleSetVolume,
    isMuted,
    callQuality,
    isOnHold,
    isSpeakerOn,
    volume,
    callRef,
    startRecording,
    stopRecording,
    showDialer,
    hideDialer,
    isDialerVisible,
    dialerPhoneNumber,
    voiceSettings,
    updateVoiceSettings: (settings) => setVoiceSettings(prev => ({ ...prev, ...settings })),
    waitingCall,
    holdAndAnswer,
    declineWaiting,
    swapCalls,
    canSwap: canSwapSlots(slots),
    registerCallWaitingBanner,
  };


  return (
    <VoiceContext.Provider value={contextValue}>
      {children}
      <ScreenCallPanel />
      {/* Call waiting banner when no in-call overlay is showing its own. */}
      {waitingCall && callWaitingBanners === 0 && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] w-[min(26rem,calc(100vw-2rem))]">
          <CallWaitingBanner />
        </div>
      )}
      {/* Hide incoming call dialog and post-call summary on mobile — mobile has its own call UI */}
      {showIncomingDialog && incomingCallerInfo && !window.location.pathname.startsWith('/mobile') && (
        <IncomingCallDialog
          isOpen={showIncomingDialog}
          onAccept={acceptCall}
          onReject={rejectCall}
          callerInfo={incomingCallerInfo}
          callerInsights={callerInsights}
        />
      )}
      {!window.location.pathname.startsWith('/mobile') && (
        <PostCallSummaryDialog
          isOpen={showPostCallSummary}
          onClose={() => {
            setShowPostCallSummary(false);
            setCompletedCallSid(null);
          }}
          callSid={completedCallSid}
        />
      )}
      {/* Hide global dialer on mobile pages — mobile has its own call UI */}
      {showActiveCallDialer && !window.location.pathname.startsWith('/mobile') && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl">
            <GlobalDialer
              onClose={() => setShowActiveCallDialer(false)}
              isModal={false}
              showNavigation={false}
              initialNumber={incomingCallerInfo?.number || callState.number}
              contactName={incomingCallerInfo?.name}
            />
          </div>
        </div>
      )}
      {isDialerVisible && !window.location.pathname.startsWith('/mobile') && (
        <div className="fixed bottom-6 right-6 z-50" data-global-dialer>
          <div className="w-80 bg-white border border-gray-700 shadow-2xl rounded-2xl overflow-hidden">
            <GlobalDialer
              onClose={hideDialer}
              isModal={true}
              showNavigation={true}
              initialNumber={dialerPhoneNumber}
            />
          </div>
        </div>
      )}
      <DialerBar />
    </VoiceContext.Provider>
  );
}

export const useVoice = (): VoiceContextType => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
};
